import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MessageCircle } from "lucide-react";
import type { Booking } from "@/types";

interface ConfirmedBookingData extends Partial<Booking> {
  chatUrl: string;
  is_now_booking?: boolean;
}

interface BookingConfirmationProps {
  booking: ConfirmedBookingData;
  onBookAnother: () => void;
}

export const BookingConfirmation = ({ booking, onBookAnother }: BookingConfirmationProps) => {
  const formatDateTime = (dt?: string) => {
    if (!dt) return null;
    try {
      const d = new Date(dt);
      return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
        + ' at ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } catch { return dt; }
  };

  const formattedTime = formatDateTime(booking.booking_datetime);

  return (
    <Card className="max-w-2xl mx-auto shadow-lg" data-testid="booking-confirmation-card">
      <CardContent className="pt-8 pb-8 text-center space-y-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <MessageCircle className="w-8 h-8 text-green-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Booking Confirmed!</h2>
          <p className="text-gray-500 mt-1">{booking.service_type} - {booking.duration_minutes} min</p>
          {booking.is_now_booking ? (
            <p className="text-lg font-bold text-red-600 mt-1" data-testid="booking-datetime">IMMEDIATELY</p>
          ) : formattedTime ? (
            <p className="text-sm font-medium text-gray-700 mt-1" data-testid="booking-datetime">{formattedTime}</p>
          ) : null}
          <p className="text-xl font-semibold text-pink-600 mt-2">Total: {"\u0E3F"}{booking.price_thb}</p>
          {booking.taxi_surcharge && booking.taxi_surcharge > 0 && (
            <p className="text-sm text-orange-600 mt-1">+ Taxi surcharge: {"\u0E3F"}{booking.taxi_surcharge}</p>
          )}
          {booking.assigned_therapist_name && (
            <p className="text-sm text-gray-600 mt-1">Therapist: {booking.assigned_therapist_name}</p>
          )}
          {booking.reassignment_message && (
            <p className="text-sm text-blue-600 mt-1">{booking.reassignment_message}</p>
          )}
        </div>
        <div className="bg-blue-50 rounded-lg p-4 space-y-3">
          <p className="font-medium text-gray-800">Chat with Angel Massage</p>
          <p className="text-sm text-gray-600">Use this link to message us about your booking:</p>
          <a
            href={booking.chatUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-pink-500 hover:bg-pink-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            data-testid="open-chat-btn"
          >
            <MessageCircle className="w-5 h-5" /> Open Chat
          </a>
          <p className="text-xs text-gray-400 mt-2">Bookmark this link to chat with us anytime</p>
        </div>
        <Button
          variant="outline"
          onClick={onBookAnother}
          className="mt-4"
          data-testid="book-another-btn"
        >
          Book Another Session
        </Button>
      </CardContent>
    </Card>
  );
};
